/**
 * Pick the API path segment and module-id field for an unpublish call.
 *
 * Both `db.courses` and `db.external_modules` are addressed by the same
 * `POST /api/master/content/{type}/{id}/unpublish` endpoint (see
 * backend/routes/master_admin.py), but the `type` segment differs:
 *   - "course"         -> id field is `course_id`
 *   - "external_course" -> id field is `module_id` (the underlying collection
 *                          is external_modules)
 *
 * Splitting these two picks out of the component makes them testable without
 * mounting React, and keeps the component code short and obvious.
 *
 * @param {object} item The row being unpublished. Must have either a
 *                       `course_id` or a `module_id`.
 * @param {"courses"|"external_courses"} contentType The form's content-type
 *                       toggle. When omitted, the type is inferred from the
 *                       item (which is what the published lists pass).
 * @returns {{ type: "course"|"external_course", id: string, label: string }}
 *          `label` is the human-friendly title for toasts and dialogs.
 */
export function pickUnpublishTarget(item, contentType) {
  const id =
    (contentType === 'courses' && (item?.course_id || item?.module_id)) ||
    item?.module_id ||
    item?.course_id ||
    null;
  if (contentType === 'courses') {
    return { type: 'course', id, label: item?.title || item?.name || 'Untitled' };
  }
  if (contentType === 'external_courses') {
    return { type: 'external_course', id, label: item?.title || item?.name || 'Untitled' };
  }
  // No explicit contentType: infer from the shape - external items carry
  // module_id without course_id.
  const inferred = item?.module_id && !item?.course_id
    ? 'external_course'
    : 'course';
  return { type: inferred, id, label: item?.title || item?.name || 'Untitled' };
}
